// AboutSection.js
import React from "react";
import { Box, Typography, Avatar, IconButton, Link } from "@mui/material";
import { motion } from "framer-motion";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";

// Import the TypingText component
import TypingText from "./TypingText";

// Import founder image from assets folder
import founderImg from "../assets/founder.jpg";

const fadeUpVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

function AboutSection() {
  return (
    <Box
      id="about"
      sx={{
        width: "100%",
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 4, md: 8 },
        background: "radial-gradient(circle at top left, #112244, #0c1e3c)",
        color: "#fff",
        fontFamily: "'Space Grotesk', 'Poppins', sans-serif",
      }}
    >
      {/* Section Title */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          mb: 6,
          color: "#ffcb05",
          textShadow: "0 0 6px #ffcb05",
          textAlign: "center",
        }}
      >
        About Spark AI Edutech Solutions
      </Typography>

      {/* Container: Vision/Mission + Founder Card */}
      <Box
        sx={{
          maxWidth: "1100px",
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 6,
          justifyContent: "space-between",
        }}
      >
        {/* Left Side: Vision & Mission */}
        <Box
          sx={{
            flex: 2,
            color: "#fff",
          }}
        >
          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
            style={{ marginBottom: 32 }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 2, color: "#ffcb05" }}
            >
              Our Vision
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Our special unique vision is to train students in their comfortable
              language while delivering advanced tech courses and skills. We aim
              to create an inclusive learning environment that empowers every
              student to excel in the AI-driven future.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2 }}>
              We envision a world where students from all backgrounds have access
              to quality education that fosters creativity, critical thinking,
              and hands-on innovation.
            </Typography>
          </motion.div>

          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
            transition={{ delay: 0.3 }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 2, color: "#ffcb05" }}
            >
              Our Mission
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Deliver practical, industry-aligned education that equips students
              with real-world AI and development skills. We strive to bridge the
              gap between academia and industry through immersive training,
              mentorship, and internship opportunities.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 2 }}>
              Our mission is to empower students to become confident problem
              solvers and lifelong learners prepared for the rapidly evolving
              technology landscape.
            </Typography>
          </motion.div>
        </Box>

        {/* Right Side: Founder Profile Card */}
        <motion.div
          variants={fadeUpVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
          style={{ flex: 1 }}
        >
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderRadius: 4,
              p: 4,
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              textAlign: "center",
              color: "#fff",
            }}
          >
            <Avatar
              src={founderImg} // Using imported image here
              alt="Founder"
              sx={{
                width: 130,
                height: 130,
                mx: "auto",
                mb: 3,
                boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                border: "3px solid #ffcb05",
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 1, color: "#ffcb05" }}
            >
              K SUMANTH YADAV
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", mb: 3, color: "#eee" }}
            >
              <TypingText
                text="Founder & CEO, Spark AI Edutech Solutions"
                speed={100}
              />
            </Typography>

            {/* Social Links */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Link
                href="https://www.instagram.com/darksky_moonu?igsh=MXBhM3E3a3Y5anVwaQ=="
                target="_blank"
                rel="noopener"
                underline="none"
              >
                <IconButton sx={{ color: "#ffcb05" }}>
                  <InstagramIcon />
                </IconButton>
              </Link>
              <Link
                href="https://www.linkedin.com/in/sumanth-k-60041b269/"
                target="_blank"
                rel="noopener"
                underline="none"
              >
                <IconButton sx={{ color: "#ffcb05" }}>
                  <LinkedInIcon />
                </IconButton>
              </Link>
              <Link
                href="https://github.com/Sumanth071"
                target="_blank"
                rel="noopener"
                underline="none"
              >
                <IconButton sx={{ color: "#ffcb05" }}>
                  <GitHubIcon />
                </IconButton>
              </Link>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}

export default AboutSection;
